import { useEffect, useRef, useState } from "react";
import Reveal from "./Reveal";
import { Star, Instagram, ChevronLeft, ChevronRight } from "lucide-react";
import { SITE } from "@/lib/site-data";

const REVIEWS = [
  {
    quote:
      "LaCretia did so good! She is so so sweet and amazing at what she does. I love my Korean lash lift and tint and my wax!!",
    service: "Korean Lash Lift + Wax",
  },
  {
    quote:
      "LaCretia was friendly and professional. She provided a calming environment. I got the Korean skincare facial and a lash lift/tint. Talk about AMAZING! I would highly recommend!",
    service: "Korean Facial + Lash Lift",
  },
  {
    quote:
      "Amazing job! Highly recommend! My lashes honestly have never looked better, I will be telling all my friends and family! I have gotten so many compliments, and I have told them where I went. She is so sweet and was able to get me in within a few days of wanting my lashes done, she is on time, very nice, is very knowledgeable! 1000/10 recommend!! :)",
    service: "Korean Lash Lift",
  },
  {
    quote:
      "I always have a wonderful experience at Skin Refinery! She does an amazing job with my eyebrow waxing, tinting, and lamination. She takes her time, pays attention to detail, and always makes sure my brows look their best. The atmosphere is welcoming and professional, and I always leave feeling confident and taken care of. I highly recommend Skin Refinery to anyone looking for quality brow services and excellent customer care!",
    service: "Brow Lamination + Tint",
  },
  {
    quote:
      "What an amazing experience. It was an absolute joy from the first moment she stepped out to meet us. We were greeted with the warmest welcome and she made sure my son was comfortable and really had a great understanding of what her plan was for his skin. She got straight into the consultation and initial treatment. She made sure to make him feel comfortable with the process from start to finish, even eliminating steps she could tell would make him uncomfortable with this being his first time getting a skin treatment. La’Cretia was top tier. There is no other way to put it. My son walked out with his skin glowing. Most importantly he felt good about the possibility of what is to come. My heart is so full. Thank you.",
    service: "Skin Treatment",
  },
];

function Stars() {
  return (
    <div className="flex items-center justify-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={13}
          className="text-[color:var(--sr-gold)]"
          fill="currentColor"
          strokeWidth={0}
        />
      ))}
    </div>
  );
}

export default function ClientLove() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = REVIEWS.length;
  const timer = useRef(null);

  const next = () => setIndex((i) => (i + 1) % total);
  const prev = () => setIndex((i) => (i - 1 + total) % total);

  // Auto-advance every 7s (paused on hover)
  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(next, 7000);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, paused]);

  return (
    <section
      id="results"
      data-testid="client-love-section"
      className="relative bg-[color:var(--sr-black)] py-20 lg:py-24 overflow-hidden"
      aria-label="Client testimonials"
    >
      {/* Full-bleed background image */}
      <div className="absolute inset-0">
        <img
          src="/images/reviews-bg.png"
          alt="Moody dark backdrop with a luxury perfume bowl"
          loading="lazy"
          className="w-full h-full object-cover"
          style={{ objectPosition: "right center" }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, #050505 0%, #050505 30%, rgba(5,5,5,0.88) 60%, rgba(5,5,5,0.55) 100%)",
          }}
        />
      </div>

      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 relative z-10">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal>
            <p className="eyebrow">Real Results. Real Clients.</p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-[52px] mt-3 text-[color:var(--sr-ivory)] tracking-[-0.01em]">
              Client Love
            </h2>
          </Reveal>
        </div>

        {/* Carousel */}
        <Reveal delay={140}>
          <div
            className="mt-10 lg:mt-14 relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="reviews-carousel"
          >
            {/* Single active slide — height grows to fit content, no overlap with controls */}
            <figure
              key={index}
              data-testid={`review-slide-${index}`}
              className="text-center px-4 sm:px-10 max-w-3xl mx-auto animate-[sr-fade-up_600ms_cubic-bezier(0.22,1,0.36,1)_forwards]"
              style={{ opacity: 0 }}
            >
              <Stars />
              <blockquote className="mt-5 font-serif italic text-[color:var(--sr-ivory)] text-[15px] md:text-[17px] leading-[1.75] md:leading-[1.8]">
                “{REVIEWS[index].quote}”
              </blockquote>
              <figcaption className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10.5px] tracking-[0.24em] uppercase">
                <span className="text-[color:var(--sr-beige)]">
                  Verified Client
                </span>
                <span className="hidden sm:inline h-[1px] w-6 bg-[color:var(--sr-gold)]/60" />
                <span className="text-[color:var(--sr-gold)]">
                  {REVIEWS[index].service}
                </span>
              </figcaption>
            </figure>

            {/* Controls — always below the review, never overlapping */}
            <div className="mt-10 flex items-center justify-center gap-6">
              <button
                onClick={prev}
                aria-label="Previous review"
                data-testid="reviews-prev"
                className="w-10 h-10 border border-[var(--sr-border-strong)] flex items-center justify-center text-[color:var(--sr-ivory)] hover:border-[color:var(--sr-gold)] hover:text-[color:var(--sr-gold)] transition-colors bg-[#050505]/60 backdrop-blur-sm"
              >
                <ChevronLeft size={16} strokeWidth={1.5} />
              </button>

              <div className="flex items-center gap-2" role="tablist">
                {REVIEWS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    aria-label={`Show review ${i + 1}`}
                    data-testid={`reviews-dot-${i}`}
                    className={`h-[6px] transition-all duration-500 rounded-full ${
                      i === index
                        ? "w-8 bg-[color:var(--sr-gold)]"
                        : "w-2 bg-[color:var(--sr-beige)]/30 hover:bg-[color:var(--sr-beige)]/60"
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={next}
                aria-label="Next review"
                data-testid="reviews-next"
                className="w-10 h-10 border border-[var(--sr-border-strong)] flex items-center justify-center text-[color:var(--sr-ivory)] hover:border-[color:var(--sr-gold)] hover:text-[color:var(--sr-gold)] transition-colors bg-[#050505]/60 backdrop-blur-sm"
              >
                <ChevronRight size={16} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={220} className="mt-10 text-center">
          <a
            href={SITE.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="see-more-reviews-btn"
            className="btn-outline inline-flex"
          >
            <Instagram size={14} strokeWidth={1.5} />
            More on Instagram
          </a>
        </Reveal>
      </div>
    </section>
  );
}
